import { ApiSet, BaseResponse, IndexApiSet, useDownloadApi, useIndexApi, usePutApi } from 'utils/network/api_hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { Result, ResultSearchForm } from 'entities/result'
import { ResultStatus, Sample } from 'entities/sample'
import { AlcoholPerWeek, BloodType, FrequencyValue, Smoking } from 'entities/background'
import { Cancer } from 'entities/product_item'
import { PagingResponse } from 'entities'
import { Form, useEffectSkipFirst } from 'utils/hooks'

type TestResultV2Response = BaseResponse & {
    result: Result
    sample: Sample
    smoothRoc?: {
        fpr: number[]
        tpr: number[]
        sensitivity: number[]
        specificity: number[]
        thresholds: number[]
        rocScore: number[]
    }
}

export type TestResultV2Form = {
    productItemId?: number
    markerCts?: { [key: string]: number }
    hkCts?: { [key: string]: number }
    enabled?: boolean
    useCt?: boolean
    baseScore?: number
    age?: number
    height?: number
    weight?: number
    deliveryDate?: string
    gender?: 'MALE' | 'FEMALE'

    symptoms?: string[]

    resultStatus?: ResultStatus
    overTemperature?: boolean
    includeBlood?: boolean
    menstruation?: boolean
    pregnancy?: boolean
    quantityStatusNg?: boolean
    differentContainer?: boolean
    nonUrine?: boolean

    personaMiddle?: number
    personaHigh?: number
    useCache?: boolean
    highPersonalRiskMagnification?: number
    enablePlotImage?: boolean
    ppvMode?: boolean
    upperLimitOfPersonalRatio?: number
    lowerLimitOfPersonalRatio?: number
}

export type OvaryTestResultV2Form = TestResultV2Form & {
    birthCount?: number
    takingPillYears?: number
    endometriosis?: boolean
    breastCancer?: boolean
    familyOvary?: boolean
    familyBreast?: boolean
}

export type BreastTestResultV2Form = TestResultV2Form & {
    exercise?: FrequencyValue
    smoking?: Smoking
    alcoholPerWeek?: AlcoholPerWeek
    ageAtFirstBirth?: number
    birthCount?: number
    mammography?: boolean
    mammographyResult?: boolean
    diabetes?: boolean
    firstDegreeRelative?: number
    secondDegreeOrHigher?: number
}

export type LungTestResultV2Form = TestResultV2Form & {
    smoking?: Smoking
    smokingAmount?: number
    smokingYears?: number
    noSmokingPeriod?: number
    familySmoking?: boolean
    COPD?: boolean
    pneumonia?: boolean
    tuberculosis?: boolean
    lungFamilyHistory?: boolean
}

export type StomachTestResultV2Form = TestResultV2Form & {
    stomachFamilyHistory?: boolean
    helicobacter?: boolean
    chronicGastritis?: boolean
    gastricUlcer?: boolean
    smoking?: Smoking
    noSmokingPeriod?: number
    eatingHabitsOfSaltedFood?: FrequencyValue
}

export type ColorectalTestResultV2Form = TestResultV2Form & {
    colorectalFamilyHistory?: boolean
    ulcerativeColitis?: boolean
    smoking?: Smoking
    smokingAmount?: number
    smokingYears?: number
    noSmokingPeriod?: number
    alcoholPerWeek?: AlcoholPerWeek
    exercise?: FrequencyValue
}

export type EsophagusTestResultV2Form = TestResultV2Form & {
    esophagusFamilyHistory?: boolean
    smoking?: Smoking
    smokingAmount?: number
    smokingYears?: number
    noSmokingPeriod?: number
    alcoholPerWeek?: AlcoholPerWeek
    eatingHabitsOfVegetablesAndFruits?: FrequencyValue
}

export type PancreasTestResultV2Form = TestResultV2Form & {
    bloodType?: BloodType
    firstDegreeRelative?: number
    smoking?: Smoking
    smokingAmount?: number
    smokingYears?: number
    noSmokingPeriod?: number
    alcoholPerWeek?: AlcoholPerWeek
    diabetes?: boolean
    chronicPancreatitis?: boolean
    pancreaticCyst?: boolean
    gastricUlcer?: boolean
    periodontalDisease?: boolean
}

type TestResultV2Params = {
    current: TestResultV2Form
    previous: TestResultV2Form
    previous2: TestResultV2Form
}

type InspectionMethod = 'PCR' | 'NGS'

export function useFetchTestResultV2Api(): ApiSet<TestResultV2Response> & { execute: (inspectionMethod: InspectionMethod, params: TestResultV2Params) => void } {
    const apiSet = usePutApi<TestResultV2Response, TestResultV2Params>(
        new CraifHttpClient(),
        {
            initialResponse: { result: {}, sample: {} },
        },
        { formatJson: true }
    )

    const execute = (inspectionMethod: InspectionMethod, params: TestResultV2Params) => {
        const apiPath = inspectionMethod === 'NGS' ? `ngs_results/test_v2/` : `results/test_v2/`
        apiSet.execute(apiPath, params)
    }

    return { ...apiSet, execute: execute }
}

export function useFetchPlotImageApi(): ApiSet<TestResultV2Response> & { execute: (inspectionMethod: InspectionMethod, params: { json: string }) => void } {
    const apiSet = useDownloadApi<TestResultV2Response>(new CraifHttpClient(), {
        initialResponse: { result: {}, sample: {} },
    })

    const execute = (inspectionMethod: InspectionMethod, params: { json: string }) => {
        const apiPath =  inspectionMethod === 'NGS' ? `ngs_results/plot_image/` :  `results/plot_image/`
        apiSet.execute(apiPath, 'plot_image', params)
    }

    return { ...apiSet, execute: execute }
}

export type PPVForm = {
    age?: number
    cancer?: Cancer
    gender?: 'MALE' | 'FEMALE'
}

export type PPVResponse = BaseResponse & {
    results: {
        score: number
        sensitivity: number
        specificity: number
        ppv: number
        prevalence: number
    }[]
}
export function useFetchPPVApi(): ApiSet<PPVResponse> & { execute: (params: PPVForm) => void } {
    const apiSet = usePutApi<PPVResponse, PPVForm>(
        new CraifHttpClient(),
        {
            initialResponse: { results: [] },
        },
        { formatJson: true }
    )

    const execute = (params: PPVForm) => {
        const apiPath = `ngs_results/ppv/`
        apiSet.execute(apiPath, params)
    }

    return { ...apiSet, execute: execute }
}

type ResultsResponse = PagingResponse & {
    results: Result[]
}

export function useFetchResultsApi(searchForm?: Form<ResultSearchForm>): IndexApiSet<ResultsResponse> & { execute: () => void } {
    const apiPath = 'ngs_results/'
    const api = useIndexApi<ResultsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object.page || 1, perPage: searchForm?.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}
